import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";

const db = supabase as unknown as any;

/** ref_id → its stable question_id (dev code, else ref_id) + catalog metadata. */
export type CatalogXref = { questionId: string; section: string | null; tClass: string | null };
export type CatalogCrosswalk = Map<string, CatalogXref>;

// Catalog notes are 'dev:<code>' (often with a suffix); extract the bare code.
function devCodeFromNotes(notes: string | null | undefined): string | null {
  if (!notes || !notes.startsWith("dev:")) return null;
  const rest = notes.slice(4).trim();
  const m = /^([A-Za-z]+-\d+[a-z]?)/.exec(rest);
  return m ? m[1] : rest.split(/[\s(]/)[0] || null;
}

/**
 * Survey catalog crosswalk for the editor's prod_data writes: maps each survey
 * leaf `ref_id` to the `question_id` prod_data is keyed by (the catalog dev code,
 * else the ref_id), plus section/t_class for the stored entry. Cached app-wide.
 */
export function useSurveyCatalogCrosswalk() {
  return useQuery<CatalogCrosswalk>({
    queryKey: ["survey-catalog-crosswalk"],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await db
        .from("survey_question_catalog_prod")
        .select("ref_id, section_title, t_class, notes");
      if (error) throw error;
      const map: CatalogCrosswalk = new Map();
      for (const c of (data ?? []) as Array<Record<string, string | null>>) {
        if (!c.ref_id) continue;
        map.set(c.ref_id, {
          questionId: devCodeFromNotes(c.notes) ?? c.ref_id,
          section: c.section_title,
          tClass: c.t_class,
        });
      }
      return map;
    },
  });
}
